import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Card, Col, Row, Space } from 'antd';
import dayjs from 'dayjs';
import TableContextProvider, { useTableControls } from 'common/components/table/table-context';
import TableBase from 'common/components/table/table-base';
import { SearchFilter } from 'common/components/table/filters/search-filter/search-filter';
import { ColumnVisibility } from 'common/components/table/filters/column-visibility/column-visibility';
import EiqTabs from 'common/components/tabs/eiq-tabs';
import { FilterTypesEnum } from 'common/enums/filter-types.enum';
import { rentalReservationFeeTypePriorityMap } from 'common/enums/rental-reservation-fee-type.enum';
import { RentalReservationStatusEnum } from 'common/enums/rental-reservation-status.enum';
import rentalReservationService from 'common/services/rental-reservation-service/rental-reservation.service';
import { IRentalReservationRead } from 'common/services/rental-reservation-service/rental-reservation.service.types';
import {
  filterColumnsByPersonalization,
  getTablePersonalizationColumnsData,
  getTablePersonalizationSortData,
  personalizationDefaultTaleSort,
} from 'common/components/table/table-helpers';
import userPersonalizationStore from 'common/stores/user-personalization/user-personalization.store';
import styles from '../../rental-portal.module.scss';
import { rentalReservationsInitialColumns } from './rental-reservations.config';

const TABLE_NAME = 'rental-reservations';
const ALL_TAB_KEY = 'All';

const sortByPriority = (reservations: IRentalReservationRead[]): IRentalReservationRead[] => {
  return [...reservations].sort((a, b) => {
    const priorityDiff = rentalReservationFeeTypePriorityMap[b.feeType] - rentalReservationFeeTypePriorityMap[a.feeType];
    return priorityDiff !== 0 ? priorityDiff : dayjs(b.createdAt).valueOf() - dayjs(a.createdAt).valueOf();
  });
};

export const RentalReservations = observer(() => {
  const [reservations, setReservations] = useState<IRentalReservationRead[]>([]);

  const tableControls = useTableControls({
    clientSide: true,
    data: reservations,
    initialColumns: filterColumnsByPersonalization(rentalReservationsInitialColumns, TABLE_NAME),
    defaultSort: personalizationDefaultTaleSort(TABLE_NAME),
    onUpdateColumns: async (columns: any[], sortBy: any) => {
      await userPersonalizationStore.updateTableColumnsPersonalization({
        tableName: TABLE_NAME,
        tableColumns: getTablePersonalizationColumnsData(columns),
        tableSort: getTablePersonalizationSortData(sortBy),
      });
    },
  });

  useEffect(() => {
    rentalReservationService.getRentalReservations().then((data) => setReservations(sortByPriority(data)));
  }, []);

  const countByStatus = (status: RentalReservationStatusEnum) => reservations.filter((reservation) => reservation.status === status).length;

  const tabItems = [
    { key: ALL_TAB_KEY, label: `All (${reservations.length})` },
    { key: RentalReservationStatusEnum.Pending, label: `Pending (${countByStatus(RentalReservationStatusEnum.Pending)})` },
    { key: RentalReservationStatusEnum.Approved, label: `Approved (${countByStatus(RentalReservationStatusEnum.Approved)})` },
    { key: RentalReservationStatusEnum.Rejected, label: `Rejected (${countByStatus(RentalReservationStatusEnum.Rejected)})` },
  ];

  const handleTabChange = (key: string) => {
    if (key === ALL_TAB_KEY) {
      tableControls.resetFilter('AND');
    } else {
      tableControls.setFilter('status', { operator: FilterTypesEnum.Equals, value: key }, 'AND');
    }
  };

  return (
    <TableContextProvider controls={tableControls}>
      <Card className="eiq-card eiq-card-border full-card">
        <Row className="eiq-card-header">
          <Col span={24}>
            <h1 className="h1">Rental Reservations</h1>
            <span className={styles.pageDescription}>
              Approve reservation applications from owners and agencies. Sorted by requested review priority: Express first, then Expedited,
              then Standard — newest submissions on top within each tier.
            </span>
          </Col>
        </Row>
        <EiqTabs className={styles.statusTabs} defaultActiveKey={ALL_TAB_KEY} items={tabItems} onChange={handleTabChange} />
        <Row gutter={8} align="middle" style={{ marginBottom: '16px' }}>
          <Col flex="auto">
            <SearchFilter
              placeholder="Search by property, renter, or reservation #"
              rulesForColumn={{
                id: FilterTypesEnum.Contains,
                address: FilterTypesEnum.Contains,
                renterName: FilterTypesEnum.Contains,
              }}
            />
          </Col>
          <Col>
            <Space size={8}>
              <ColumnVisibility isColumnReorderAllow={true} />
            </Space>
          </Col>
        </Row>
        <TableBase onSelected={tableControls.setSelectedRow} selectFirstRowByDefault />
      </Card>
    </TableContextProvider>
  );
});
